import { useState } from 'react';
import { toast } from 'sonner';
import { useDeleteVehicle, useSaveVehicle, useVehicles } from '@/hooks/useVehiclesQuery';
import VehicleCard from '@/components/vehicles/VehicleCard';
import VehicleForm from '@/components/vehicles/VehicleForm';
import type { Vehicle, VehicleDraft } from '@/types/Vehicle';

export default function Vehicles() {
  const { data: vehicles, isLoading, error } = useVehicles();
  const saveMutation = useSaveVehicle();
  const deleteMutation = useDeleteVehicle();
  const [editing, setEditing] = useState<Vehicle | 'new' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave(draft: VehicleDraft) {
    setSaveError(null);
    try {
      await saveMutation.mutateAsync(draft);
      setEditing(null);
      toast.success('Vehicle saved', { description: `${draft.year} ${draft.make} ${draft.model}` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSaveError(msg);
      toast.error('Save failed', { description: msg });
    }
  }

  async function handleDelete(v: Vehicle) {
    if (!confirm(`Delete "${v.year} ${v.make} ${v.model}"? Wishlist clears too.`)) return;
    try {
      await deleteMutation.mutateAsync(v.id);
      toast.success('Vehicle deleted', { description: `${v.year} ${v.make} ${v.model}` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error('Delete failed', { description: msg });
    }
  }

  return (
    <div className="app">
      <div className="main">
        <div className="row between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className="eyebrow">My Vehicles · Saved Builds</div>
            <h1 className="h-title" style={{ margin: 0 }}>Saved Vehicles</h1>
          </div>
          {editing === null && (
            <button type="button" className="btn sm" onClick={() => setEditing('new')}>
              ＋ Add Vehicle
            </button>
          )}
        </div>
        <p className="lead" style={{ marginBottom: 28 }}>
          Save vehicles so long restorations track everything sourced and still needed. VIN decode
          + per-vehicle part wishlists ship in a follow-up sprint.
        </p>

        {editing !== null && (
          <div className="card-elev" style={{ marginBottom: 28 }}>
            <h2 className="h-section">
              {editing === 'new' ? 'Add a Vehicle' : `Edit ${editing.year} ${editing.make} ${editing.model}`}
            </h2>
            <VehicleForm
              initial={editing === 'new' ? null : editing}
              busy={saveMutation.isPending}
              onSubmit={handleSave}
              onCancel={() => {
                setEditing(null);
                setSaveError(null);
              }}
            />
            {saveError && <div className="auth-error" style={{ marginTop: 14 }}>{saveError}</div>}
          </div>
        )}

        {isLoading && <div className="empty-state">Loading vehicles…</div>}
        {error && (
          <div className="auth-error">
            Failed to load vehicles: {error instanceof Error ? error.message : String(error)}
          </div>
        )}

        {!isLoading && (vehicles?.length ?? 0) === 0 && editing === null && (
          <div
            className="empty-state"
            style={{ background: 'var(--bg-1)', border: '1px dashed var(--line-2)', padding: 60 }}
          >
            <div style={{ fontFamily: 'var(--display)', fontSize: 28, color: 'var(--text-dim)' }}>
              No vehicles saved yet.
            </div>
            <p style={{ margin: '12px 0 18px' }}>
              Add your first build to keep notes and wishlists alongside it.
            </p>
            <button type="button" className="btn" onClick={() => setEditing('new')}>
              ＋ Add First Vehicle
            </button>
          </div>
        )}

        {vehicles && vehicles.length > 0 && (
          <div className="parts-grid">
            {vehicles.map((v) => (
              <VehicleCard
                key={v.id}
                vehicle={v}
                onEdit={() => setEditing(v)}
                onDelete={() => handleDelete(v)}
                deleting={deleteMutation.isPending && deleteMutation.variables === v.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
